/**
 * Portuguese (pt-BR) — canonical dictionary.
 *
 * This is the source of truth. All other locales must implement the SAME
 * keys. The `Translations` type in `src/i18n/index.ts` is derived from this
 * file, so TypeScript will fail the build if `en.ts` or `es.ts` drift.
 */

const pt = {
  meta: {
    htmlLang: 'pt-BR',
  },
  common: {
    loading: 'Carregando...',
    buy: 'Comprar',
    buyNow: 'Comprar Agora',
    startNow: 'Comece Agora',
    downloadApp: 'Baixe o App',
    getMyChip: 'Quero Meu Chip',
    iWantMyChip: 'Quero meu chip',
    close: 'Fechar',
    cancel: 'Cancelar',
    unlimited: 'Ilimitado',
  },
  layout: {
    loading: 'Carregando...',
    errorTitle: 'Erro ao carregar configurações',
  },
  topBar: {
    customerService: 'Atendimento',
    helpCenter: 'Central de Ajuda',
    recharges: 'Recargas',
    portability: 'Portabilidade',
    defaultPromo: 'AGORA VOCÊ PODE VER SUAS LINHAS NO NOSSO NOVO APP',
    downloadNowCta: 'BAIXE AGORA',
  },
  header: {
    home: 'Início',
    plans: 'Planos',
    portability: 'Portabilidade',
    advantages: 'Vantagens',
    recharge: 'Recarga',
    customerService: 'Atendimento',
    helpCenter: 'Central de Ajuda',
  },
  hero: {
    defaultHeadline: 'Você mais',
    defaultHeadlineBold: 'conectado',
    defaultSubtitle: 'A operadora que entende de conectar você com o mundo!',
    defaultPlanLabel: 'PLANO PRO',
    defaultPlanData: '45',
    defaultPlanUnit: 'gb',
    defaultPlanFeatures: 'Internet + SMS + Ligações',
    defaultPlanBenefits: 'E mais 12 benefícios incríveis',
    defaultPlanPrice: 'R$39,99',
    defaultPlanInstallment: 'ou 29,90 no plano anual',
    defaultAppCta: 'Comece agora mesmo com o app',
    defaultCurvedText: 'Controle tudo com o app White Telecom',
    defaultDisclaimer:
      'Oferta válida por tempo limitado. Consulte condições, cobertura e disponibilidade na sua região. Valores sujeitos a alteração sem aviso prévio. Imagens meramente ilustrativas.',
    defaultPromoText: 'Aproveite nossa promoção com +15GB',
    defaultPriceLabel: 'Apenas',
    fallbackApps: 'Whatsapp Ilimitado, Waze Ilimitado',
  },
  plans: {
    tag: 'Planos e Benefícios',
    titleLine1: 'Muito mais que um plano,',
    titleLine2: 'possibilidades para você fazer mais',
    mostPopular: 'Mais Procurado',
    appsIncluded: 'Aplicativos Inclusos',
    perMonth: 'por mês',
  },
  benefits: {
    tag: 'Benefícios',
    titleLine1: 'Benefícios que já vêm',
    titleLine2: 'com seu plano',
    items: [
      { title: 'Whatsapp Ilimitado', description: 'Use todos os dias sem descontar da sua internet.' },
      { title: 'Planos Sem Fidelidade', description: 'Liberdade para cancelar quando quiser, sem multa.' },
      { title: 'Atendimento Que Resolve', description: 'Suporte rápido e direto, sem enrolação.' },
      { title: 'Acumule a Internet Que Não Usar', description: 'A internet que sobra volta para você.' },
      { title: 'Conexão 5G e Rápida do Brasil', description: 'Mais velocidade e estabilidade onde estiver.' },
      { title: 'Experiência 100% Digital', description: 'Resolva tudo pelo app, sem burocracia.' },
      { title: 'Seu Chip Físico ou Digital', description: 'Escolha entre chip físico ou eSIM.' },
      { title: 'Ligações e SMS Infinitas', description: 'Fale e envie mensagens sem limites.' },
    ],
  },
  features: {
    tag: 'Benefícios',
    titleLine1: 'Pensamos em cada detalhe',
    titleLine2: 'para você',
    physicalChip: 'Chip Físico',
    eSim: 'eSIM',
    chipTitle: 'Ative seu chip em minutos.',
    chipDesc: 'Escolha entre chip físico ou eSIM e comece a usar na hora.',
    portabilityTag: 'Portabilidade',
    portabilityTitle: 'Traga seu número para a {{brand}}.',
    portabilityDesc: 'Escolha entre chip físico ou eSIM e comece a usar na hora.',
    aboutTag: 'SOBRE NÓS',
    aboutTitleLine1: 'Uma operadora',
    aboutTitleLine2: 'feita para',
    aboutTitleLine3: 'simplificar sua',
    aboutTitleLine4: 'vida.',
  },
  appSection: {
    titleLine1: 'Controle tudo pelo',
    titleLine2: 'aplicativo.',
    appBadges: ['Saldo', 'Recargas', 'Linhas', 'Pagamentos', 'Portabilidade', 'Clube de Descontos'],
    activateHint: 'Baixe o app e ative seu chip em minutos.',
    socialProofTag: 'Gente',
    socialProofTitle: 'Cada vez mais pessoas estão escolhendo a {{brand}}.',
    socialProofFallbackBrand: 'nossa operadora',
    socialProofFooter: 'Milhares de pessoas já estão conectadas.',
    ratingSuffix: 'de avaliação no app',
    ratingFooter: 'Avaliação do nosso app nas lojas de aplicativo.',
    purpleTitleLine1: 'Internet móvel sem',
    purpleTitleLine2: 'complicação.',
  },
  faq: {
    tag: 'Perguntas Frequentes',
    titleLine1: 'Está em dúvida?',
    titleLine2: 'Podemos te ajudar',
    subtitle: 'Deixamos aqui as perguntas mais frequentes que recebemos',
    viewAnswer: 'Ver Resposta',
    fallback: [
      { category: 'Portabilidade', question: 'Consigo fazer a portabilidade de qualquer operadora?', answer: 'Sim! Você pode trazer seu número de qualquer operadora para a nossa rede de forma totalmente gratuita e sem burocracias, direto pelo aplicativo.' },
      { category: 'Cobertura', question: 'Vocês têm cobertura na minha cidade?', answer: 'Possuímos a maior cobertura do Brasil, alcançando 100% dos municípios através da nossa rede parceira com tecnologia 4G e 5G.' },
      { category: 'Portabilidade', question: 'Posso manter meu número atual?', answer: 'Com certeza! Durante o processo de ativação do seu novo chip ou eSIM, você opta por manter o seu número, e nós cuidamos de toda a transição.' },
      { category: 'Planos', question: 'O que acontece quando minha internet acabar?', answer: 'Você continuará conectado! A navegação sofre uma redução de velocidade, mas você não perde acesso. Se preferir, pode contratar pacotes adicionais instantaneamente pelo app.' },
      { category: 'Assinatura', question: 'Posso cancelar a qualquer momento?', answer: 'Sim. Acreditamos na liberdade dos nossos clientes. Nossos planos não possuem fidelidade ou taxas escondidas para cancelamento.' },
    ],
    legalTag: 'Legal',
  },
  footer: {
    support: 'Suporte',
    helpCenter: 'Central de Ajuda',
    customerService: 'Atendimento',
    copyrightSuffix: 'Todos os direitos reservados',
    cnpjLabel: 'CNPJ',
    defaultCompany: 'White Telecom',
  },
  marquee: {
    defaultText: 'Você muito mais conectado',
  },
  rechargeModal: {
    title: 'Fazer Recarga',
    successTitle: 'Recarga solicitada!',
    copyLink: 'Copiar Link',
    payNow: 'Pagar Agora',
    linkCopied: 'Link copiado para a área de transferência!',
    phoneLabel: 'Número do Telefone',
    phonePlaceholder: '(11) 99999-9999',
    planLabel: 'Plano',
    loadingPlans: 'Carregando planos...',
    submitButton: 'Recarregar',
    errorInvalidPhone: 'Informe um número de telefone válido com DDD.',
    errorSelectPlan: 'Selecione um plano.',
    errorGeneric: 'Erro ao processar a recarga. Tente novamente.',
  },
  portabilityModal: {
    title: 'Solicitar Portabilidade',
    successTitle: 'Portabilidade solicitada!',
    protocolLabel: 'Protocolo',
    yourNumberLabel: 'Seu número na {{brand}}',
    yourNumberFallbackBrand: 'operadora',
    donorNumberLabel: 'Número a ser portado (operadora atual)',
    donorOperatorLabel: 'Operadora atual',
    loadingOperators: 'Carregando operadoras...',
    submitButton: 'Solicitar Portabilidade',
    errorInvalidContel: 'Informe um número Contel válido com DDD.',
    errorInvalidDonor: 'Informe o número atual (doador) válido com DDD.',
    errorSelectOperator: 'Selecione a operadora atual.',
    errorGeneric: 'Erro ao solicitar portabilidade. Tente novamente.',
  },
  legalModal: {
    placeholderContent: 'Conteúdo não configurado.',
  },
  page404: {
    title: '404',
    subtitle: 'Página não encontrada',
    back: 'Voltar ao início',
  },
  languageSwitcher: {
    label: 'Idioma',
    pt: 'Português',
    en: 'Inglês',
    es: 'Espanhol',
  },
  whatsapp: {
    salesMessage: 'Gostaria de adquirir um novo plano da {{brand}}',
    salesFallbackBrand: 'operadora',
  },
};

export default pt;
